import type { TFunction } from 'i18next';
import { dateOnlyInTz, todayDateOnlyInTz } from '@/shared/utils/dateUtils';

/**
 * Groups by calendar day in the user's configured timezone (`ui.store`), not the browser's —
 * the key is a `YYYY-MM-DD` string, so it sorts and compares correctly without further parsing.
 */
export function groupActivityByDay<T>(
  items: T[],
  getCreatedAt: (item: T) => string,
): { key: string; items: T[] }[] {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = dateOnlyInTz(getCreatedAt(item));
    const group = map.get(key);
    if (group) group.push(item);
    else map.set(key, [item]);
  }
  return Array.from(map.entries()).map(([key, groupItems]) => ({ key, items: groupItems }));
}

/** `key` is a `YYYY-MM-DD` calendar-day string, already resolved in the user's configured timezone by `groupActivityByDay`. */
export function getActivityDayLabel(t: TFunction, key: string): string {
  const todayKey = todayDateOnlyInTz();
  if (key === todayKey) return t('activity.dateGroups.today');

  // Both keys are calendar-day strings in the same timezone — diffing their UTC-midnight
  // representations gives a correct day count without any DST/offset arithmetic.
  const keyDate = new Date(`${key}T00:00:00Z`);
  const todayDate = new Date(`${todayKey}T00:00:00Z`);
  const diffDays = Math.round((todayDate.getTime() - keyDate.getTime()) / 86_400_000);

  if (diffDays === 1) return t('activity.dateGroups.yesterday');
  if (diffDays < 7) return t('activity.dateGroups.daysAgo', { count: diffDays });

  const [year, month, date] = key.split('-').map(Number);
  return new Date(year, month - 1, date).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });
}
